/**
 * One driver's devices: whether the office manages the account, how many devices it may work
 * from, the devices it has, one of the firm's free ones to give it, and a QR code for his own
 * phone. Taking back one of the firm's frees it for the next driver; taking back his own phone
 * forgets it, since it is nobody else's to have.
 *
 * The two switches are saved the moment they change, like the fleet's own switch: each says
 * what happens next — a password that stops working, a second phone taken away — and a Save
 * button would let somebody believe it had not happened yet.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { describe } from '../settings/errors';
import { useTenantFormats } from '../shell/formats';
import { Banner, Button, ConfirmDialog, Dialog, Pill, Section, Select, SettingRow, Switch } from '../ui';
import { devicesApi, type DeviceDriver, type FirmDevice } from './api';
import { deviceTitle } from './words';
import { QrTicket } from './QrTicket';

export function ServiceDeviceDialog({
  driverId,
  driverName,
  startWithQr = false,
  onClose,
}: {
  driverId: string;
  driverName: string;
  /** Straight to the QR code, for a driver created a moment ago to be given a phone. */
  startWithQr?: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const [driver, setDriver] = useState<DeviceDriver | null>(null);
  const [showQr, setShowQr] = useState(startWithQr);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Asked first: the driver is signed out of it at once.
  const [releasing, setReleasing] = useState<DeviceDriver['devices'][number] | null>(null);

  // The firm's devices nobody has, to give this driver one without a QR code. A driver's own
  // phone is never free, so never among them.
  const [free, setFree] = useState<FirmDevice[]>([]);
  const [giving, setGiving] = useState('');

  const load = useCallback(async () => {
    try {
      const [mine, all] = await Promise.all([devicesApi.driver(driverId), devicesApi.list()]);

      setDriver(mine);
      setFree(all.filter((device) => device.retiredAt === null && device.driver === null && !device.personal));
    } catch (failure) {
      setError(describe(failure, t, 'devices.couldNotLoad'));
    }
  }, [driverId, t]);

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect -- fetching, not cascading
    void load();
  }, [load]);

  async function save(signsIn: boolean, alternate: boolean) {
    setBusy(true);
    setError(null);

    try {
      setDriver(
        await devicesApi.setPolicy(driverId, {
          signsInOnServiceDevice: signsIn,
          mayAlternateDevices: alternate,
        }),
      );
    } catch (failure) {
      setError(describe(failure, t, 'devices.couldNotSave'));
    } finally {
      setBusy(false);
    }
  }

  async function release(deviceId: string) {
    setReleasing(null);
    setBusy(true);
    setError(null);

    try {
      await devicesApi.release(deviceId);
      // The scanned code's "registered and signed in" is about a list that has just changed;
      // left showing, it would say so of a device that is no longer in it.
      setShowQr(false);
      await load();
    } catch (failure) {
      setError(describe(failure, t, 'devices.couldNotSave'));
    } finally {
      setBusy(false);
    }
  }

  async function give() {
    setBusy(true);
    setError(null);

    try {
      await devicesApi.assign(giving, driverId);
      setGiving('');
      await load();
    } catch (failure) {
      setError(describe(failure, t, 'devices.couldNotSave'));
    } finally {
      setBusy(false);
    }
  }

  const scanned = useCallback(() => void load(), [load]);

  return (
    <Dialog
      open
      wide
      title={t('devices.dialog.title', { name: driverName })}
      onClose={onClose}
      actions={
        <Button variant="quiet" onClick={onClose}>
          {t('devices.close')}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {error !== null && <Banner tone="danger">{error}</Banner>}

        {driver === null ? (
          <p className="m-0 text-secondary text-ink-3">{t('app.loading')}</p>
        ) : (
          <>
            <Section title={t('devices.dialog.how')}>
              <SettingRow label={t('devices.dialog.managed')} help={t('devices.dialog.managedHelp')}>
                <Switch
                  label={t('devices.dialog.managed')}
                  checked={driver.signsInOnServiceDevice}
                  disabled={busy}
                  onChange={(next) => void save(next, driver.mayAlternateDevices)}
                />
              </SettingRow>

              <SettingRow
                last
                label={t('devices.dialog.alternate')}
                help={
                  driver.mayAlternateDevices
                    ? t('devices.dialog.alternateOn')
                    : t('devices.dialog.alternateOff')
                }
              >
                <Switch
                  label={t('devices.dialog.alternate')}
                  checked={driver.mayAlternateDevices}
                  disabled={busy}
                  onChange={(next) => void save(driver.signsInOnServiceDevice, next)}
                />
              </SettingRow>
            </Section>

            <Section
              title={t('devices.dialog.phones')}
              actions={
                !showQr && (
                  <Button size="sm" disabled={busy} onClick={() => setShowQr(true)}>
                    {t('devices.dialog.addPhone')}
                  </Button>
                )
              }
            >
              {driver.devices.length === 0 ? (
                <p className="m-0 text-secondary text-ink-3">{t('devices.dialog.noPhones')}</p>
              ) : (
                <ul className="m-0 p-0 list-none flex flex-col gap-2">
                  {driver.devices.map((device) => (
                    <li key={device.id} className="flex items-center justify-between gap-3">
                      <span>
                        {deviceTitle(t, device)}{' '}
                        <Pill tone={device.personal ? 'neutral' : 'info'}>
                          {t(device.personal ? 'devices.owner.personal' : 'devices.owner.firm')}
                        </Pill>
                        <span className="block text-secondary text-ink-3">
                          {device.lastSessionAt === null
                            ? t('devices.dialog.neverUsed', { date: formats.moment(device.enrolledAt) })
                            : t('devices.dialog.lastUsed', { date: formats.moment(device.lastSessionAt) })}
                        </span>
                      </span>
                      <Button size="sm" disabled={busy} onClick={() => setReleasing(device)}>
                        {t(device.personal ? 'devices.dialog.remove' : 'devices.dialog.release')}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}

              {free.length > 0 && (
                <div className="mt-3 flex items-end gap-2">
                  <Select
                    aria-label={t('devices.dialog.giveFree')}
                    value={giving}
                    onChange={(event) => setGiving(event.target.value)}
                  >
                    <option value="">{t('devices.dialog.giveFree')}</option>
                    {free.map((device) => (
                      <option key={device.id} value={device.id}>
                        {deviceTitle(t, device)}
                      </option>
                    ))}
                  </Select>
                  <Button size="sm" disabled={busy || giving === ''} onClick={() => void give()}>
                    {t('devices.dialog.give')}
                  </Button>
                </div>
              )}

              {(showQr || (giving !== '' && !driver.mayAlternateDevices && driver.devices.length > 0)) && (
                <div className="mt-3 flex flex-col gap-2">
                  {!driver.mayAlternateDevices && driver.devices.length > 0 && (
                    <Banner tone="warn">{t('devices.dialog.replaces')}</Banner>
                  )}
                  {showQr && <QrTicket target={{ driverId }} onScanned={scanned} />}
                </div>
              )}
            </Section>
          </>
        )}
      </div>

      <ConfirmDialog
        open={releasing !== null}
        title={t(releasing?.personal ? 'devices.dialog.confirmRemoveTitle' : 'devices.dialog.confirmReleaseTitle')}
        confirm={t(releasing?.personal ? 'devices.dialog.remove' : 'devices.dialog.release')}
        cancel={t('devices.dialog.keep')}
        onConfirm={() => {
          if (releasing !== null) void release(releasing.id);
        }}
        onClose={() => setReleasing(null)}
      >
        {releasing !== null &&
          t(releasing.personal ? 'devices.dialog.confirmRemove' : 'devices.dialog.confirmRelease', {
            device: deviceTitle(t, releasing),
            name: driverName,
          })}
      </ConfirmDialog>
    </Dialog>
  );
}
